/**
 * Per-rep softphone audio tuning (PD-14902).
 *
 * These are the knobs a rep can adjust to compensate for a quiet or
 * over-processed microphone and a too-soft far party — the softphone
 * equivalent of Teams' in-call input/output volume handles. All of it
 * is applied in the browser; none of it changes the call topology
 * (the conference bridge, PSTN routing, or codec).
 *
 * Two independent mechanisms back these settings, both provided by the
 * Twilio Voice SDK:
 *   - `gain` values drive WebAudio processors inserted on the outgoing
 *     (local) and incoming (remote) streams — see the browser-side
 *     `SoftphoneAudioProcessors`.
 *   - the `capture` constraints drive `AudioHelper.setAudioConstraints`,
 *     which controls the getUserMedia processing the browser applies
 *     to the microphone BEFORE the audio reaches us.
 *
 * Kept in libs/core (DOM-free) so `SoftphoneDeviceModel` can reference
 * the shapes without pulling WebAudio types into the platform-agnostic
 * layer.
 */

/**
 * Browser microphone-capture processing. These map 1:1 onto the
 * standard `MediaTrackConstraints` booleans and are forwarded to the
 * SDK's `setAudioConstraints`.
 *
 * Defaults mirror the browser's own defaults (all enabled), so the
 * feature is inert until a rep changes something. `noiseSuppression`
 * is the highest-value toggle: Chrome's suppression is aggressive and
 * a common cause of a "muffled / underwater" quality on voice calls.
 */
export interface SoftphoneAudioCaptureModel {
	/** Chrome's noise suppression. Off can markedly de-muffle a voice. */
	noiseSuppression: boolean;
	/** Browser auto-gain. Off pairs with an explicit `inputGain` for a
	 *  predictable level instead of the browser pumping the mic. */
	autoGainControl: boolean;
	/** Echo cancellation. Almost always wanted on for a speaker+mic
	 *  setup; exposed for completeness and headset edge cases. */
	echoCancellation: boolean;
}

/**
 * The full audio-tuning state for a rep. Persisted per rep (browser
 * side) and applied to each call's device on registration and on
 * change.
 */
export interface SoftphoneAudioSettingsModel {
	/**
	 * Digital makeup gain on the outgoing microphone stream. `1` is
	 * unity (no change). Above `1` boosts a quiet mic; the processor
	 * pairs the gain with light compression so louder does not simply
	 * mean a louder noise floor. A browser cannot change the OS input
	 * level, so this software gain is how "make me louder to them" is
	 * achieved.
	 */
	inputGain: number;
	/**
	 * Playback gain on the incoming (remote) stream. `1` is unity.
	 * Above `1` makes the far party louder to the rep.
	 */
	outputVolume: number;
	/**
	 * When true, the incoming stream is run through gentle compression
	 * and a presence lift to counteract the narrow, distant quality of
	 * a phone-network leg. Opt-in (default false) so existing behavior
	 * is unchanged until a rep enables it.
	 */
	enhanceReceivedAudio: boolean;
	/** Microphone-capture processing constraints. */
	capture: SoftphoneAudioCaptureModel;
}

/** Unity gain — no amplification. */
export const SOFTPHONE_UNITY_GAIN = 1;

/**
 * Clamp bounds for the gain sliders. `0` = silence, `2` = +6 dB.
 * Bounded so a rep cannot drive the processor into hard clipping.
 */
export const SOFTPHONE_MIN_GAIN = 0;
export const SOFTPHONE_MAX_GAIN = 2;

/**
 * Default tuning: unity gain both ways, enhancement off, and the three
 * mic-capture settings ON — matching the browser's own defaults.
 *
 * These three (noise suppression, auto-gain, echo cancellation) give the
 * best result on a HEALTHY connection. Extended testing (varying WiFi
 * distance) showed the "muffled" degradation was driven by weak WiFi —
 * packet loss and jitter — not by the processing itself: near the access
 * point every combination sounded excellent; far from it, quality fell
 * off and the processing's own artifacts compounded the network's,
 * making a bad link sound worse. So the right posture is all-on by
 * default (they help on a good connection and are the browser standard),
 * treat the ROOT cause — connection quality — separately, and keep these
 * as per-rep toggles for the rare weak-link or very-noisy edge case.
 */
export function getDefaultSoftphoneAudioSettings(): SoftphoneAudioSettingsModel {
	return {
		inputGain: SOFTPHONE_UNITY_GAIN,
		outputVolume: SOFTPHONE_UNITY_GAIN,
		enhanceReceivedAudio: false,
		capture: {
			noiseSuppression: true,
			autoGainControl: true,
			echoCancellation: true,
		},
	};
}

/** Clamp a gain value into the allowed range, coercing NaN to unity. */
export function clampSoftphoneGain(value: number): number {
	if (Number.isNaN(value)) {
		return SOFTPHONE_UNITY_GAIN;
	}
	return Math.min(SOFTPHONE_MAX_GAIN, Math.max(SOFTPHONE_MIN_GAIN, value));
}
